import {
	Component,
	OnInit,
	CUSTOM_ELEMENTS_SCHEMA,
	NO_ERRORS_SCHEMA,
	inject,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { ConfirmDialogComponent } from '../shared/confirm-dialog/confirm-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';

import { SHARED_IMPORTS } from '../shared/shared-imports';
import { ContentService } from '../../services/content.service';
import { UserNotificationPreferencesComponent } from '../user-notification-preferences/user-notification-preferences.component';


@Component({
	selector: 'user-detail-component',
	standalone: true,
	templateUrl: 'components/user-detail/user-detail.component.html',
	imports: [...SHARED_IMPORTS, MatSlideToggleModule, MatSnackBarModule, MatIconModule, UserNotificationPreferencesComponent],
	schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class UserDetailComponent implements OnInit {
	user: any;
	currentLanguage: any;
	currentUser = false;
	userSignedIn: any;
	roles: any[] = [];
	rolesFiltered: any[] = [];
	roleLowerThanManager: any;
	currentRole: any;
	readMode = false;
	languages: any[] = [];
	ready = false;
	isUserDisabled = false;

	private content = inject(ContentService);
	private http = inject(HttpClient);
	private router = inject(Router);
	private route = inject(ActivatedRoute);
	private snackbar = inject(MatSnackBar);
	private dialog = inject(MatDialog);

	constructor() { }

	async ngOnInit(): Promise<void> {
		const userId = this.route.snapshot.params['userId'];

		await this.getLanguages();
		await this.getUser(userId);
		await this.getRoles();
		
		// Set currentLanguage after both languages and user are loaded
		this.setCurrentLanguage();
		
		this.isUserDisabled = this.isDisabledUserDetail(this.user);

		this.ready = true;
	}

	getContent(id: string): string {
		return this.content.getContent(id);
	}

	setCurrentLanguage(): void {
		if (this.user && this.user.AppLanguageId && this.languages.length > 0) {
			this.currentLanguage = this.languages.find(lang => lang.Id === this.user.AppLanguageId);
		} else {
			console.warn('UserDetail: Could not set currentLanguage - user:', this.user, 'languages:', this.languages);
		}
	}

	goBack(): void {
		// TODO: Implement proper navigation history tracking if needed
		// For now, default to UserList
		this.router.navigate(['/UserList']);
	}

	getUser(userId?: number): Promise<any> {
		if (userId) {
			return Promise.all([
				this.http.get(`/api/user/User/${userId}`).toPromise().then((res: any) => {
					this.user = res;
					this.user.Active = this.user.Active === true || this.user.Active === 'true';
				}),
				this.http.get('/api/user/CurrentUser').toPromise().then((res: any) => {
					this.userSignedIn = res;
				}),
			]).then(() => { });
		} else {
			return this.http.get('/api/user/CurrentUser').toPromise().then((res: any) => {
				this.user = res;
				this.currentUser = true;
				this.user.Active = this.user.Active === true || this.user.Active === 'true';
			});
		}
	}

	validatePhoneNo(event: KeyboardEvent): boolean {
		const isAsterisk = event.which === 42 || event.keyCode === 42;
		if (isAsterisk) {
			event.preventDefault();
		}
		return !isAsterisk;
	}


	getPermission(user: any): void {
		this.http
			.get('/api/user/GetUserPermissionForChangingUserActivation', {
				params: { UserId: user.Id },
			})
			.subscribe();
	}

	getRoles(): Promise<any> {
		return this.http.get('/api/user/Roles').toPromise().then((res: any) => {
			this.roles = res;
			this.rolesFiltered = [...this.roles];
			this.roleLowerThanManager = this.roles.find((r) => r.RoleName === 'Manager');

			if (this.user) {
				this.currentRole = this.roles.find((r) => r.Id === this.user.GlobalAppRoleId);
				this.readMode = this.user.GlobalAppRoleId.localeCompare(this.roleLowerThanManager.Id) === 1;

				if (this.user.GlobalAppRoleId.localeCompare(this.roleLowerThanManager.Id) === 0) {
					this.rolesFiltered = this.roles.filter((r) => r.RoleName !== 'Administrator');
				}
			}
		});
	}

	isDisabledUserDetail = (user: any): boolean => {
		return (
			this.userSignedIn &&
			user &&
			this.userSignedIn.GlobalAppRoleId.localeCompare(user.GlobalAppRoleId) === 1
		);
	}


	getLanguages(): Promise<any> {
		return this.http.get('/api/user/Languages').toPromise().then((res: any) => {
			this.languages = res;
			this.languages.forEach((lang) => {
				lang.LanguageName = this.content.getContent('UserDetail.' + lang.LanguageName);
			});
		});
	}

	changeUserStatus(user: any): void {
		this.getPermission(user);

		const dialogRef = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.content.getContent(
					'User.AreYouSureYouWantTo' + (user.Active ? 'Activate' : 'Deactivate')
				),
				text: user.Email,
				okText: this.content.getContent('Standard.Yes'),
				cancelText: this.content.getContent('Standard.No')
			}
		});

		dialogRef.afterClosed().subscribe(confirmed => {
			if (confirmed) {
				this.http
					.get(`/api/user/${user.Active ? 'Activate' : 'Deactivate'}`, {
						params: { UserId: user.Id }
					})
					.subscribe();
			} else {
				// Revert status if user canceled
				user.Active = !user.Active;
			}
		});


	}

	changeUserRole(user: any): void {
		const snackMsg = this.getContent('User.UserRoleHasBeenChanged')
		this.http
			.get('/api/user/ChangeRole', {
				params: { userId: user.Id, roleId: user.GlobalAppRoleId },
			})
			.subscribe(() => {
				this.snackbar.open(snackMsg, '', { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
	
			});
	}

	changeUserLanguage(user: any): void {
		this.http
			.get('/api/user/ChangeAppLanguage', {
				params: { userId: user.Id, appLanguageId: this.currentLanguage.Id },
			})
			.subscribe(() => {
				this.content.clearAllAndReload();
			});
	}

	changeUserPhoneNo(user: any): void {
		
		const snackMsg = this.getContent('User.UserPhoneHasBeenChanged')
		if (this.user?.PhoneNo !== undefined) {
			this.http
				.get('/api/user/ChangePhoneNo', {
					params: { userId: user.Id, phoneNo: user.PhoneNo },
				})
				.subscribe(() => {
					this.snackbar.open(snackMsg, '', { duration: 3000, horizontalPosition: 'center', verticalPosition: 'bottom' });
	
				});
		}
	}
}
